import {
  Blockchain,
  Env,
  FaceError,
  FaceErrorCode,
  isEthlikeNetwork,
  Network,
  NetworkChainIdMap,
} from '@haechi-labs/face-types';
import { ethers } from 'ethers';

export function isMainnet(network: Network) {
  switch (network) {
    case Network.ETHEREUM:
    case Network.POLYGON:
    case Network.TEZOS:
    case Network.BNB_SMART_CHAIN:
    case Network.KLAYTN:
    case Network.SOLANA:
    case Network.BORA:
    case Network.NEAR:
    case Network.APTOS:
    case Network.MEVERSE:
    case Network.PSM:
    case Network.YOOLDO_VERSE:
    case Network.MCH_VERSE:
    case Network.HEDERA:
    case Network.DEFI_VERSE:
    case Network.KROMA:
      // case Network.ASM:
      return true;
    default:
      return false;
  }
}

export function getBlockchainFromNetwork(network?: Network): Blockchain {
  switch (network) {
    case Network.ETHEREUM:
    case Network.SEPOLIA:
      return Blockchain.ETHEREUM;
    case Network.POLYGON:
    case Network.MUMBAI:
      return Blockchain.POLYGON;
    case Network.TEZOS:
    case Network.GHOSTNET:
      return Blockchain.TEZOS;
    case Network.BNB_SMART_CHAIN:
    case Network.BNB_SMART_CHAIN_TESTNET:
      return Blockchain.BNB_SMART_CHAIN;
    case Network.KLAYTN:
    case Network.BAOBAB:
      return Blockchain.KLAYTN;
    case Network.SOLANA:
    case Network.SOLANA_DEVNET:
      return Blockchain.SOLANA;
    case Network.BORA:
    case Network.BORA_TESTNET:
      return Blockchain.BORA;
    case Network.NEAR:
    case Network.NEAR_TESTNET:
      return Blockchain.NEAR;
    case Network.APTOS:
    case Network.APTOS_TESTNET:
      return Blockchain.APTOS;
    case Network.MEVERSE:
    case Network.MEVERSE_TESTNET:
      return Blockchain.MEVERSE;
    case Network.PSM:
    case Network.PSM_TESTNET:
      return Blockchain.PSM;
    case Network.PSM_TESTNET_DEV:
      return Blockchain.PSM_DEV;
    case Network.PSM_TESTNET_TEST:
      return Blockchain.PSM_TEST;
    case Network.HOME_VERSE:
    case Network.HOME_VERSE_TESTNET:
      return Blockchain.HOME_VERSE;
    case Network.YOOLDO_VERSE:
    case Network.SAND_VERSE:
      return Blockchain.YOOLDO_VERSE;
    case Network.OASYS:
    case Network.OASYS_TESTNET:
      return Blockchain.OASYS;
    case Network.MCH_VERSE:
    case Network.MCH_VERSE_TESTNET:
      return Blockchain.MCH_VERSE;
    case Network.HEDERA:
    case Network.HEDERA_TESTNET:
      return Blockchain.HEDERA;
    case Network.DEFI_VERSE:
    case Network.DEFI_VERSE_TESTNET:
      return Blockchain.DEFI_VERSE;
    case Network.KROMA:
    case Network.KROMA_SEPOLIA:
      return Blockchain.KROMA;
    // case Network.ASM:
    //   return Blockchain.ASM;
    case Network.ASM_QA:
      return Blockchain.ASM_QA;
    case Network.ASM_TEST:
      return Blockchain.ASM_TEST;
    case Network.ASM_DEV:
      return Blockchain.ASM_DEV;
    default:
      return Blockchain.ETHEREUM;
  }
}

export function getIframeUrl(env?: Env, iframeUrl?: string): string {
  if (iframeUrl != null) {
    return iframeUrl;
  }
  switch (env) {
    case Env.Local:
      return 'http://localhost:3333';
    case Env.Dev:
      return 'https://app.dev.facewallet.xyz';
    case Env.StageTest:
      return 'https://app.stage-test.facewallet.xyz';
    case Env.ProdTest:
      return 'https://app.test.facewallet.xyz';
    case Env.StageMainnet:
      return 'https://app.stage.facewallet.xyz';
    case Env.ProdMainnet:
      return 'https://app.facewallet.xyz';
    default:
      return 'https://app.facewallet.xyz';
  }
}

export function getDefaultEnv(network?: Network): Env {
  if (!network) {
    return Env.ProdMainnet;
  }
  if (
    network === Network.SEPOLIA ||
    network === Network.MUMBAI ||
    network === Network.BAOBAB ||
    network === Network.GHOSTNET ||
    network === Network.BNB_SMART_CHAIN_TESTNET ||
    network === Network.BORA_TESTNET ||
    network === Network.SOLANA_DEVNET ||
    network === Network.NEAR_TESTNET ||
    network === Network.APTOS_TESTNET ||
    network === Network.MEVERSE_TESTNET ||
    network === Network.PSM_TESTNET ||
    network === Network.PSM_TESTNET_DEV ||
    network === Network.PSM_TESTNET_TEST ||
    network === Network.HOME_VERSE_TESTNET ||
    network === Network.SAND_VERSE ||
    network === Network.OASYS_TESTNET ||
    network === Network.MCH_VERSE_TESTNET ||
    network === Network.HEDERA_TESTNET ||
    network === Network.DEFI_VERSE_TESTNET ||
    network === Network.KROMA_SEPOLIA ||
    network === Network.ASM_QA ||
    network === Network.ASM_TEST ||
    network === Network.ASM_DEV
  ) {
    return Env.ProdTest;
  }
  return Env.ProdMainnet;
}

export function getNetwork(network: Network | number | string): Network {
  const hexRegex = /^0x[0-9A-Fa-f]*$/;
  if (typeof network === 'string' && hexRegex.test(network)) {
    const chainId = parseInt(network, 16);

    if (NetworkChainIdMap.hasOwnProperty(chainId)) {
      return NetworkChainIdMap[chainId] as Network;
    } else {
      throw new FaceError({
        message: `Invalid chainId: ${chainId}`,
        code: FaceErrorCode.UNSUPPORTED_CHAIN,
      });
    }
  }

  if (typeof network === 'number') {
    if (NetworkChainIdMap.hasOwnProperty(network)) {
      return NetworkChainIdMap[network] as Network;
    } else {
      throw new FaceError({
        message: `Invalid chainId: ${network}`,
        code: FaceErrorCode.UNSUPPORTED_CHAIN,
      });
    }
  }

  if (Object.values(Network).includes(network as Network)) {
    return network as Network;
  }

  throw new FaceError({
    message: `Invalid network parameter: ${network}`,
    code: FaceErrorCode.UNSUPPORTED_CHAIN,
  });
}

export const getEthlikeChainIdFromNetwork = (network: Network) => {
  return isEthlikeNetwork(network) ? ethers.utils.hexlify(NetworkChainIdMap[network]) : 0;
};

export function isEveryNetworkOnSameEnv(networks: Network[]) {
  return (
    networks.every((network) => isMainnet(network)) ||
    networks.every((network) => !isMainnet(network))
  );
}
